import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `Tooltip` primitive. Backs the `src/antd/Tooltip` seam's shadcn half
 * (`src/antd/Tooltip.shadcn.tsx`); nothing else should import it, and nothing
 * reaches it until someone flips the design toggle.
 *
 * Ported from the `update-design` reference branch. Two things about it are
 * decisions rather than transcription:
 *
 * THE CONTAINER. `TooltipPrimitive.Portal` with NO `container` mounts the
 * bubble on `document.body`, which is also antd's default (antd's Tooltip
 * portals to body unless a `getPopupContainer` says otherwise). The seam
 * therefore keeps the body portal for every call site and only leaves it when
 * one explicitly asks — see the container note in `src/antd/Tooltip.shadcn`.
 * Pinning the popup to the trigger's parent, the way the `Select` seam does,
 * is what makes an overflow/transform ancestor able to CLIP it; a tooltip that
 * is invisible inside a scroll container is worse than an ugly one.
 *
 * THE STACKING. `z-[1070]` mirrors antd's own tooltip z-index, so a bubble
 * raised from inside a modal still sits above it — in either design mode,
 * since an un-ported antd surface can be on screen at the same time.
 *
 * THE FILL is `--foreground` on `--background`, NOT `--primary`. shadcn's
 * registry writes this bubble as `bg-primary text-primary-foreground`, which
 * reads as a dark neutral chip only because the stock zinc theme's `--primary`
 * IS zinc-950. This app repoints `--primary` at the WasteHero brand blue
 * (`src/styles/tailwind.css`), so the registry class rendered a solid blue
 * chip — the captain's report, 2026-08-26. `--foreground`/`--background` are
 * the same zinc pair the registry meant, they invert with `data-theme` exactly
 * as the registry's do (dark: a near-white chip with dark text), and they are
 * the highest-contrast pair in the palette. Body and arrow move together
 * because both read the one token.
 * ------------------------------------------------------------------------- */

function TooltipProvider({
  delayDuration = 100,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

// forwardRef: a trigger child may itself be a ref-forwarding component, and
// `asChild` clones the ref onto it.
const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentProps<typeof TooltipPrimitive.Trigger>
>((props, ref) => (
  <TooltipPrimitive.Trigger ref={ref} data-slot="tooltip-trigger" {...props} />
))
TooltipTrigger.displayName = 'TooltipTrigger'

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  showArrow = true,
  container,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  showArrow?: boolean
  /** Portal target. `undefined` = `document.body`, the default. */
  container?: HTMLElement | null
}) {
  return (
    <TooltipPrimitive.Portal container={container ?? undefined}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        // antd's Tooltip exposes role="tooltip"; Radix relies on
        // aria-describedby instead, so set it explicitly to preserve
        // getByRole('tooltip') at the ~60 call sites that assert on it.
        role="tooltip"
        sideOffset={sideOffset}
        className={cn(
          'bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 z-[1070] w-fit max-w-[250px] origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance',
          className
        )}
        {...props}
      >
        {children}
        {showArrow && (
          <TooltipPrimitive.Arrow
            data-slot="tooltip-arrow"
            className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]"
          />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
